import { Box, CircularProgress } from '@mui/material';
import { memo, Fragment, useRef, useCallback, useEffect } from 'react';
import { useSearch } from '../../SearchContextProvider';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchCardView from '../SearchCardView';
import SearchListView, { SearchListViewProps } from '../SearchListView';
import { useSearchItems } from '../SearchItemsProvider';
import SkeletonBox from 'components/generic/SkeletonBox';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import { useTranslation } from 'react-i18next';
import { ApiErrorResponse } from 'utility/types/dataProvider';

/**
 * Generic - Search results view
 * Card, Summary, List

 * Handles: view type switching
 * pagination loading and errors
 *
 * All props should be drilled & optional as data
 * can come from various sources and contexts.
 */

export type SearchViewResultsProps = {
  editorialConfig?: SearchListViewProps['editorialConfig'];
};

export const SearchViewResults = memo(
  ({ editorialConfig = undefined }: SearchViewResultsProps = {}) => {
    const { t } = useTranslation();
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const { view, clearAllQueryParams, hasNoQueryParams } = useSearch();
    const {
      isFetching,
      isLoading,
      error,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
      refetch,
    } = useSearchItems();

    // Translation strings
    const strLoading = t('blox.general.loading');

    const handleOnRowsScrollEnd = useCallback(async () => {
      if (!hasNextPage || isFetchingNextPage || isFetching) return;
      await fetchNextPage();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetching]);

    // Handles infinite scroll card view
    useEffect(() => {
      if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage || isFetching)
        return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            handleOnRowsScrollEnd();
          }
        },
        { threshold: 0.05 },
      );

      observer.observe(loadMoreRef.current);

      return () => {
        observer.disconnect();
      };
    }, [handleOnRowsScrollEnd, hasNextPage, isFetchingNextPage, isFetching]);

    /**
     * Attempt to clear/refetch to correct error state.
     * Clearing QP will trigger a refetch.
     * If no QP, just refetch
     */
    const handleErrorReset = useCallback(async () => {
      // No QP try and refetch list from API
      if (hasNoQueryParams) {
        await refetch();
        return;
      }
      // Clear all qp to trigger a refetch
      clearAllQueryParams();
    }, [clearAllQueryParams, hasNoQueryParams, refetch]);

    if (isLoading || (isFetching && !isFetchingNextPage)) {
      return (
        <SkeletonBox
          text={strLoading}
          width="100%"
          height="100%"
        />
      );
    }

    if (error) {
      const castErr = error as ApiErrorResponse;
      return (
        <ErrorAlerts
          showDetails={true}
          severity="warning"
          buttonOnClick={handleErrorReset}
          buttonText="Reset"
          buttonIcon={<ReplayIcon />}
          sx={{ m: 1 }}
          errors={[
            {
              status: `${castErr?.status || castErr?.axiosStatus}`,
              message: `${castErr?.message || castErr?.axiosMessage}`,
              fullDetails: castErr,
            },
          ]}
        />
      );
    }

    switch (view) {
      case 'card': {
        return (
          <Fragment>
            <SearchCardView />
            {hasNextPage && (
              <Box
                id="cardViewLoadMore"
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                {isFetchingNextPage && view === 'card' && <CircularProgress />}
              </Box>
            )}
          </Fragment>
        );
      }
      case 'summary':
      case 'list': {
        return <SearchListView editorialConfig={editorialConfig} />;
      }
      default: {
        return null;
      }
    }
  },
);
SearchViewResults.displayName = 'SearchViewResults';
